import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowRightLong } from "react-icons/fa6";
import userProfile from "../../assets/images/user-profile.svg";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../context/UserContext";
import { putFetcher, postPhotoFetcher } from "../../../api/fetchers";
import { endpoints } from "../../../api/endpoints";
import { useLanguage } from "../../context/LanguageContext";

interface FormikValues {
  firstName: string;
  lastName: string;
  email: string;
  openAddress: string;
  phoneNumber: string;
  companyName: string;
  identityNo: string;
  taxNumber: string;
  taxOffice: string;
  kepAddress: string;
  profilePicture: string;
}

const EditProfile: React.FC = () => {
  const { t } = useLanguage();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoutModal, setLogoutModal] = useState(false);

  const handleClickLogout = () => {
    setLogoutModal(true);
  };

  const logOutConfirm = () => {
    localStorage.removeItem("user");
    localStorage.removeItem('favoriteProductIds');
    localStorage.removeItem('access-token');
    localStorage.removeItem('basketItems');
    toast.success(t('toastMessage', 'logoutSuc'));
    setTimeout(() => {
        navigate("/");
        window.location.reload();
    }, 2000);
  };

  const initialValues: FormikValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    openAddress: user?.openAddress || "",
    phoneNumber: user?.phoneNumber || "",
    companyName: user?.companyName || "",
    identityNo: user?.identityNo || "",
    taxNumber: user?.taxNumber || "",
    taxOffice: user?.taxOffice || "",
    kepAddress: user?.kepAddress || "",
    profilePicture: user?.profilePicture || "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      firstName: Yup.string().required("Name required"),
      lastName: Yup.string().required("Surname required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email required"),
      openAddress: Yup.string().required("Address required"),
      phoneNumber: Yup.string()
        .matches(/^\d{10}$/, "Invalid phone number")
        .required("Phone number required"),
    }),
    onSubmit: async (values) => {
      try {
        const updatedFields: Partial<FormikValues> = Object.keys(values).reduce(
          (acc: any, key: any) => {
            if (
              values[key as keyof FormikValues] !==
              initialValues[key as keyof FormikValues]
            ) {
              acc[key as keyof FormikValues] =
                values[key as keyof FormikValues];
            }
            return acc;
          },
          {} as Partial<FormikValues>
        );

        if (Object.keys(updatedFields).length === 0) {
          toast.info("Değişiklik yapılmadı.");
          return;
        }

        const response = await putFetcher(endpoints.user.updateUserInfo, {
          arg: updatedFields,
        });

        if (response?.result) {
          toast.success(t('toastMessage', 'infoSuc'));
          setUser({
            ...user!,
            ...updatedFields,
          });
        } else {
          toast.error(t('toastMessage', 'infoErr'));
        }
      } catch (error) {
        toast.error(t('toastMessage', 'infoErr'));
        console.error("Error updating user information:", error);
      }
    },
  });

  useEffect(() => {
    if (user) {
      formik.setValues({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        openAddress: user.openAddress,
        phoneNumber: user.phoneNumber,
        profilePicture: user.profilePicture,
        companyName: user.companyName,
        identityNo: user.identityNo,
        taxNumber: user.taxNumber,
        taxOffice: user.taxOffice,
        kepAddress: user.kepAddress,
      });
    }
  }, [user]);


  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  {
    /* UPLOAD IMAGE */
  }
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
    
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await postPhotoFetcher(
          endpoints.updateImage.updateImageSingle,
          formData
        );

        if (response && response.data.url) {
          formik.setFieldValue("profilePicture", response.data.url);
          toast.success(t('toastMessage', 'pImageSuc'));
        } else {
          toast.error(t('toastMessage', 'pImageErr'));
        }
      } catch (error) {
        toast.error(t('toastMessage', 'pImageErr'));
        console.error("Error uploading photo:", error);
      }
    }
  };

  return (
    <div className="p-4 md:px-6 md:py-4  h-full rounded-lg overflow-y-auto">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className=" h-full max-h-full">
        <form className="space-y-4" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col md:flex-row justify-between">
            <p className="text-2xl md:text-4xl font-semibold self-center">
              {t('editProfile', 'title')}
            </p>
            <div className="text-center mt-4 md:mt-0">
              <img
                src={formik.values.profilePicture || userProfile}
                alt="Profil"
                className="rounded-full w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 object-cover"
              />
              <div>
                <button
                  type="button"
                  onClick={handleButtonClick}
                  className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-full"
                >
                  {t('editProfile', 'file')}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
              </div>
            </div>
          </div>

          {/* PERSONEL INFORMATION */}
          <p className="text-[#FFC633] font-bold text-[22px]">
          {t('editProfile', 'person')}
          </p>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <input
                type="text"
                id="firstName"
                name="firstName"
                className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
                  formik.touched.firstName && formik.errors.firstName
                    ? "border-red-500"
                    : ""
                }`}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.firstName && formik.errors.firstName ? (
                <div className="text-red-500 text-sm">
                  {formik.errors.firstName}
                </div>
              ) : null}
            </div>
            <div className="flex-1">
              <input
                type="text"
                id="lastName"
                name="lastName"
                className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
                  formik.touched.lastName && formik.errors.lastName
                    ? "border-red-500"
                    : ""
                }`}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.lastName && formik.errors.lastName ? (
                <div className="text-red-500 text-sm">
                  {formik.errors.lastName}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-500"
                      : ""
                  }`}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-red-500 text-sm">
                    {formik.errors.email}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex-1">
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? "border-red-500"
                    : ""
                }`}
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                <div className="text-red-500 text-sm">
                  {formik.errors.phoneNumber}
                </div>
              ) : null}
            </div>
          </div>

          {/* ADDRESS INFORMATION */}
          <div>
            <p className="text-[#FFC633] font-bold text-[22px] mt-8">
            {t('editProfile', 'address')}
            </p>
            <input
              type="text"
              id="openAddress"
              name="openAddress"
              className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
                formik.touched.openAddress && formik.errors.openAddress
                  ? "border-red-500"
                  : ""
              }`}
              value={formik.values.openAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.openAddress && formik.errors.openAddress ? (
              <div className="text-red-500 text-sm">
                {formik.errors.openAddress}
              </div>
            ) : null}
          </div>

          {user?.businessPackageId != "10" ? (
            <>
              {/* COMPANY INFORMATION */}
              <p className="text-[#FFC633] font-bold text-[22px] mt-8">
              {t('editProfile', 'company')}
              </p>

              <div className="flex justify-between">
                <div className="w-[49.35%]">
                  <label className="block text-sm font-medium text-gray-700">
                  {t('editProfile', 'companyName')}
                  </label>
                  <div className="relative">
                    <input
                      type="companyName"
                      id="companyName"
                      name="companyName"
                      className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2`}
                      value={formik.values.companyName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                  </div>
                </div>

                <div className="w-[49.35%]">
                  <label className="block text-sm font-medium text-gray-700">
                  {t('editProfile', 'identityNumber')}
                  </label>
                  <div className="relative">
                    <input
                      type="identityNo"
                      id="identityNo"
                      name="identityNo"
                      className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2`}
                      value={formik.values.identityNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <div className="w-[49.35%]">
                  <label className="block text-sm font-medium text-gray-700">
                  {t('editProfile', 'taxNumber')}
                  </label>
                  <div className="relative">
                    <input
                      type="taxNumber"
                      id="taxNumber"
                      name="taxNumber"
                      className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2`}
                      value={formik.values.taxNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                  </div>
                </div>
                <div className="w-[49.35%]">
                  <label className="block text-sm font-medium text-gray-700">
                  {t('editProfile', 'taxOffice')}
                  </label>
                  <div className="relative">
                    <input
                      type="taxOffice"
                      id="taxOffice"
                      name="taxOffice"
                      className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2`}
                      value={formik.values.taxOffice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                {t('editProfile', 'keepAddress')}
                </label>
                <div className="relative">
                  <input
                    type="kepAddress"
                    id="kepAddress"
                    name="kepAddress"
                    className={`mt-1 disabled:bg-gray-200 block w-full border border-gray-300 rounded-md shadow-sm p-2`}
                    value={formik.values.kepAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                  />
                </div>
              </div>
            </>
          ) : null}
          <div>
            <Link
              to="/profile/change-password"
              className="flex items-center justify-between text-primary-color font-medium text-sm mt-4"
            >
              <span> {t('editProfile', 'updatePass')}</span>
              <FaArrowRightLong className="text-primary-color text-lg" />
            </Link>
          </div>
          <div className="flex gap-4 mt-4 flex-col md:flex-row">
            <button
              type="submit"
              className="flex-1 py-2 px-4 w-full md:max-w-56 bg-primary-color text-white font-bold rounded-md hover:bg-color-pink"
            >
               {t('editProfile', 'save')}
            </button>
            <button
              type="button"
              onClick={handleClickLogout}
              className="flex-1 py-2 px-4 w-full md:max-w-56 border border-primary-color text-primary-color font-bold rounded-md hover:bg-gray-100"
            >
               {t('editProfile', 'exit')}
            </button>
          </div>
        </form>
      </div>
      {logoutModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          {/* Modal Container */}
          <div className="bg-white w-11/12 max-w-md rounded-lg shadow-lg p-6 relative">
            {/* Close Button */}
            <button
              onClick={() => setLogoutModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            {/* Modal Content */}
            <h2 className="text-xl font-semibold text-center text-gray-800">
              {t('sideBar', 'logout')}
            </h2>
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={logOutConfirm}
                className="bg-primary-color text-white px-6 py-2 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                {t('sideBar', 'yes')}
              </button>
              <button
                onClick={() => setLogoutModal(false)}
                className="bg-yellow-color text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                {t('sideBar', 'no')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
